import { useEffect, useState } from "react";
import { addPracticeLog, fetchPracticeLog, fetchPracticeProgram, loc } from "@/lib/ethicsApi";
import EthicsFrame from "./EthicsFrame";
import { useT } from "@/i18n/LanguageContext";

export default function EthicsPractice() {
  const { lang } = useT();
  const L = (v) => loc(v, lang);
  const [program, setProgram] = useState(null);
  const [logs, setLogs] = useState([]);
  const [notes, setNotes] = useState({});
  const [msg, setMsg] = useState("");

  useEffect(() => {
    fetchPracticeProgram().then((d) => setProgram(d.program)).catch(() => {});
    fetchPracticeLog().then((d) => setLogs(d.logs || [])).catch(() => {});
  }, []);

  const logPractice = async (pid) => {
    try {
      const { log } = await addPracticeLog(pid, notes[pid] || "");
      setLogs((l) => [log, ...l]);
      setNotes((n) => ({ ...n, [pid]: "" }));
      setMsg(lang === "nl" ? "Vastgelegd. Eén volledige daad van oefening." : "Recorded. One complete act of practice.");
      setTimeout(() => setMsg(""), 2500);
    } catch (e) { setMsg(e.message); }
  };

  if (!program) return <div className="min-h-screen bg-ink" />;

  return (
    <EthicsFrame title="The Instruments of Practice" crumb="Practice" testid="ethics-practice-page" canonicalPath="/practice">
      <p className="text-fog/80 font-reading max-w-2xl mb-4 leading-[1.9]">{L(program.intro)}</p>
      <p aria-live="polite" className="text-[11px] text-gold/80 h-4 mb-8" data-testid="ethics-practice-msg">{msg}</p>

      {program.sections.map((section) => (
        <section key={section.id} className="mb-12" aria-label={L(section.title)}>
          <h2 className="text-xs tracking-[0.3em] uppercase text-[#c86058] mb-5">{L(section.title)}</h2>
          <div className="grid md:grid-cols-2 gap-5">
            {section.practices.map((p) => {
              const count = logs.filter((l) => l.practiceId === p.id).length;
              return (
                <article key={p.id} className="border border-[#c8a95a]/20 bg-[#14211a] p-6 flex flex-col gap-3" data-testid={`ethics-practice-${p.id}`}>
                  <div className="flex items-baseline justify-between gap-3">
                    <h3 className="font-serif text-lg text-bone">{L(p.title)}</h3>
                    <span className="text-[10px] tracking-[0.15em] uppercase text-fog/50 whitespace-nowrap">{count > 0 ? `${count}× ${lang === "nl" ? "gedaan" : "done"}` : ""}</span>
                  </div>
                  <p className="text-sm text-fog font-reading leading-relaxed">{L(p.instruction)}</p>
                  <p className="text-xs text-[#c86058] font-reading italic">{L(p.grounding)}</p>
                  {p.safety && (
                    <p className="text-xs text-gold/85 font-reading border-l-2 border-gold/40 pl-3" data-testid={`ethics-practice-safety-${p.id}`}>{L(p.safety)}</p>
                  )}
                  <textarea rows={2} value={notes[p.id] || ""} data-testid={`ethics-practice-note-${p.id}`}
                    onChange={(e) => setNotes((n) => ({ ...n, [p.id]: e.target.value }))}
                    placeholder={lang === "nl" ? "Wat je zag of deed, in enkele zinnen…" : "What you saw or did, in a few sentences…"}
                    className="w-full bg-transparent border border-[#c8a95a]/20 text-bone font-reading p-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold/50" />
                  <button onClick={() => logPractice(p.id)} data-testid={`ethics-practice-log-${p.id}`}
                    className="self-start px-5 py-2.5 border border-[#c86058]/60 text-[#e8b8b2] text-[10px] tracking-[0.22em] uppercase hover:bg-[#1e332a] focus:outline-none focus:ring-2 focus:ring-gold/60">
                    {lang === "nl" ? "Vastleggen als gedaan" : "Record as done"}
                  </button>
                </article>
              );
            })}
          </div>
        </section>
      ))}

      <section aria-label="Practice log">
        <h2 className="text-xs tracking-[0.3em] uppercase text-[#c86058] mb-5">{lang === "nl" ? "Jouw oefenlogboek" : "Your practice log"} · {logs.length}</h2>
        <div className="space-y-2">
          {logs.slice(0, 20).map((l) => (
            <p key={l.id} className="text-sm text-fog/80 font-reading border-b border-white/5 pb-2">
              <span className="text-[10px] text-fog/45 mr-3">{(l.at || "").slice(0, 10)}</span>
              <span className="text-[#e8b8b2] mr-2">{l.practiceId}</span>{l.note}
            </p>
          ))}
        </div>
      </section>
    </EthicsFrame>
  );
}
